// Pure formatting / validation helpers for the app UI. No DOM, no state, no API.
//
// The formatters the invoice/quote/credit note DOCUMENT is built from live in
// shared/ instead, because the PDF renderer needs the identical implementation
// — they are re-exported here so app code keeps one import surface for
// formatting. Don't reimplement one of them locally: a second copy is how the
// preview came to format dates in the viewer's locale while the PDF used en-GB.
import {
  escapeHtml,
  formatSortCode,
  formatCurrencyPreviewHtml,
  toPreviewMultiline
} from "../../../shared/document-format.js";
import { CURRENCIES, currencySymbol } from "../../../shared/currencies.js";

export { escapeHtml, formatSortCode, formatCurrencyPreviewHtml, toPreviewMultiline };
export { CURRENCIES, currencySymbol };

export function toTitleCase(value) {
  return String(value)
    .trim()
    .replace(/\s+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function splitNameParts(name) {
  const normalized = String(name || "").trim().replace(/\s+/g, " ");
  if (!normalized) return { firstName: "", lastName: "" };
  const parts = normalized.split(" ");
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" ")
  };
}

export function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

// Google Ads conversion tracking
export function gtagConversion(label, extras) {
  if (typeof gtag !== "function") return;
  gtag("event", "conversion", Object.assign({ send_to: "AW-18167893742/" + label }, extras));
}

// Microsoft Advertising (Bing). The UET tag itself lives in the <head> of
// index.html and app.html, next to gtag — NOT here, and not lazily injected.
//
// An earlier version of this file injected bat.js on the first conversion, to
// keep the tag ID in one place. That was wrong: UET reads the MSCLKID off the
// landing URL and stores it as a cookie when it loads. A tag that only loads
// at conversion time never saw the landing URL, so Microsoft cannot attribute
// the conversion to the click that paid for it — and Microsoft's own tag
// verification reports no signal. Attribution beats tidiness.
//
// window.uetq is an array until bat.js swaps it for a UET instance; both accept
// .push, so this works either side of that and before the script has loaded.
function uetQueue() {
  if (typeof window === "undefined" || !window.uetq) return null;
  return window.uetq;
}

// The three conversions, defined once for the ad engines and GA4.
//
// WHY THIS EXISTS RATHER THAN PAIRED CALLS: the same three events have to reach
// Google and Microsoft from three different screens. Two parallel call sites
// kept in step by a comment is precisely the failure this repo hit on 5 Sep 2026,
// when base.css listed .landing-page and .lp-home under a comment saying "BOTH
// must be listed" and .signup-page was never added — leaving every auth screen
// 1024px wide on a phone. One function, one list, no drift. Add a fourth
// conversion here and every measurement destination gets it.
//
// That promise was half-kept: when the ga4 layer was added it was added to
// signup alone, so Google Ads and Microsoft counted first invoices and
// subscriptions while GA4 recorded people arriving and nothing they did after.
// Every conversion now names its GA4 event. If you add a fourth, give it one.
export const CONVERSIONS = {
  signup: {
    google: "dGxeCJ_IkK4cEO6dkNdD",
    microsoft: "signup",
    ga4: [
      { name: "sign_up", params: { method: "email" } },
      { name: "qualify_lead", params: { lead_source: "free_trial" } }
    ]
  },
  subscription: {
    google: "DVxLCLfSjq4cEO6dkNdD",
    microsoft: "subscription",
    // GA4's own purchase event. value, currency and transaction_id are not
    // repeated here: the call site in screens/profile.js passes them as extras,
    // which are merged over these params, and GA4 needs all three together
    // (currency without value is dropped, transaction_id is what de-duplicates).
    ga4: [{ name: "purchase", params: {} }],
  },
  firstInvoice: {
    google: "zOSkCMruubIcEO6dkNdD",
    microsoft: "first_invoice",
    // No GA4 recommended event fits "the trial finally did the thing", so this
    // is a custom one. Mark it a key event in GA4 (Admin > Events) or it stays
    // an ordinary event and never reaches the conversion reports.
    ga4: [{ name: "first_invoice_sent", params: {} }],
  },
};

export function trackConversion(key, extras) {
  const conversion = CONVERSIONS[key];
  if (!conversion) return;
  // Never from a dev machine. track.js and ga4.js both skip localhost, and a
  // conversion is the one event where a stray dev hit does real damage: these
  // are the numbers cost per signup and cost per first invoice are divided by.
  try {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") return;
  } catch {}
  if (conversion.google) gtagConversion(conversion.google, extras);
  if (Array.isArray(conversion.ga4) && typeof window !== "undefined" && typeof window.gtag === "function") {
    conversion.ga4.forEach((event) => {
      window.gtag("event", event.name, Object.assign({}, event.params, extras));
    });
  }
  const uetq = uetQueue();
  if (uetq) uetq.push("event", conversion.microsoft, extras || {});
}

export function getPasswordUpdatedCopy(rawIsoDate) {
  if (!rawIsoDate) return "Last updated recently";
  const updated = new Date(rawIsoDate);
  if (Number.isNaN(updated.getTime())) return "Last updated recently";

  const now = new Date();
  const diffMs = now.getTime() - updated.getTime();
  if (diffMs >= 0 && diffMs < 60 * 1000) {
    return "Last updated just now";
  }
  if (diffMs >= 60 * 1000 && diffMs < 60 * 60 * 1000) {
    const minutes = Math.max(1, Math.floor(diffMs / (60 * 1000)));
    return `Last updated ${minutes} min ago`;
  }

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfUpdated = new Date(updated.getFullYear(), updated.getMonth(), updated.getDate());
  const msPerDay = 24 * 60 * 60 * 1000;
  const dayDiff = Math.floor((startOfToday - startOfUpdated) / msPerDay);
  const timeLabel = formatTimeOfDay(updated);

  if (dayDiff <= 0) return `Last updated today at ${timeLabel}`;
  if (dayDiff === 1) return `Last updated yesterday at ${timeLabel}`;

  const formatted = updated.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
  return `Last updated ${formatted}`;
}

// Currency defaults to GBP so every existing single-argument call site keeps
// today's behaviour.
export function formatCurrency(rawValue, currency = "GBP") {
  const amount = Number.parseFloat(String(rawValue ?? 0));
  const safe = Number.isFinite(amount) ? amount : 0;
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: CURRENCIES.includes(String(currency || "").toUpperCase())
      ? String(currency).toUpperCase()
      : "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(safe);
}

/**
 * The same, from the integer pence the API sends. Every amount on the wire is
 * `…Pence` (iOS map, ticket 06), so this is where money becomes a number
 * again — once, at the point it is shown.
 *
 * @param {number|null|undefined} pence
 * @param {string} [currency]
 * @returns {string}
 */
export function formatPence(pence, currency = "GBP") {
  return formatCurrency((Number(pence) || 0) / 100, currency);
}

/**
 * A money field's value for a form input, from the pence the API sends:
 * "12.50", or "" when there is none. The forms are in pounds, because that is
 * what a person types; lib/api.js puts the pence back on the way out.
 *
 * @param {number|null|undefined} pence
 * @returns {string}
 */
export function poundsFieldValue(pence) {
  return pence == null || !Number.isFinite(Number(pence)) ? "" : (Number(pence) / 100).toFixed(2);
}

/**
 * The form value for a discount or deposit term, which is a percentage rate
 * for `percent` and an amount for `fixed`. Only the amount is money, so only
 * it comes from the `…ValuePence` twin.
 *
 * @param {string|null} type
 * @param {number|string|null} value
 * @param {number|null} valuePence
 * @returns {string|number}
 */
export function termFieldValue(type, value, valuePence) {
  if (String(type || "") === "fixed" && valuePence != null) return poundsFieldValue(valuePence);
  return value != null ? value : "";
}

/**
 * A line item's unit price for the compose form: the `…Pence` the API sends,
 * or the pounds a duplicate draft carries in sessionStorage, which never went
 * through the API.
 *
 * @param {{unitPrice?: number|string, unitPricePence?: number|null}} item
 * @returns {number} pounds, NaN when the item names no price
 */
export function itemUnitPrice(item) {
  if (item?.unitPricePence != null && Number.isFinite(Number(item.unitPricePence))) {
    return Number(item.unitPricePence) / 100;
  }
  return Number.parseFloat(String(item?.unitPrice ?? ""));
}

export function addDays(baseDate, days) {
  const base = baseDate instanceof Date ? baseDate : new Date(baseDate);
  const safeDays = Number.isFinite(days) ? days : 0;
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + safeDays);
  return next;
}

// "£25,650.00" for a single currency, "£25,650.00 + $1,200.00" when mixed.
// GBP leads, the rest alphabetical; an empty bucket shows £0.00 (as before).
export function formatBucketAmount(bucket) {
  const entries = Object.entries(bucket.amounts || {});
  if (!entries.length) return formatCurrency(0);
  entries.sort(([a], [b]) => (a === "GBP" ? -1 : b === "GBP" ? 1 : a.localeCompare(b)));
  return entries.map(([cur, value]) => formatCurrency(value, cur)).join(" + ");
}

// "9am", "9:30am", "2pm" — and "midday" / "midnight" for the two a 12-hour
// clock can't say without ambiguity.
//
// 24-hour reads as a machine talking: "09:00" makes a person stop and work out
// whether that's morning or night, which is the one thing a send time has to
// be obvious about. Minutes appear only when there are any, so a whole hour
// stays two characters instead of five.
export function formatTimeOfDay(date, zone = {}) {
  // formatToParts THROWS on an invalid date rather than returning something
  // useless, so this has to come first — the checks below never get a chance.
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    ...zone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date);
  const value = (type) => Number(parts.find((p) => p.type === type)?.value ?? NaN);
  const hours = value("hour");
  const minutes = value("minute");
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return "";

  if (minutes === 0 && hours === 0) return "midnight";
  if (minutes === 0 && hours === 12) return "midday";

  const suffix = hours < 12 ? "am" : "pm";
  // 0 and 12 both show as 12 on a 12-hour clock; every other hour is h % 12.
  const display = hours % 12 === 0 ? 12 : hours % 12;
  return minutes === 0
    ? `${display}${suffix}`
    : `${display}:${String(minutes).padStart(2, "0")}${suffix}`;
}

// "Today at 9am", "Yesterday at 2:30pm", "5 Sep 2026 at 11am" — the timestamp
// on an activity line.
//
// One copy. This was three byte-identical private functions (the invoice and
// quote status drawers, and Team), which is how a change to one of them
// becomes a difference between screens rather than a change to the app.
//
// The reader's own timezone is right here, unlike a scheduled send: these are
// records of something that already happened, and "when did that happen" is
// asked from where you're sitting.
export function formatActivityTimestamp(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return "";
  const time = formatTimeOfDay(d);
  const now = new Date();
  // Calendar days, NOT elapsed milliseconds. "Today" and "Yesterday" are
  // positions on a calendar, and `Math.floor((now - d) / dayMs)` gets two
  // ordinary cases wrong: an event stamped seconds in the future by a server
  // clock a touch ahead goes negative and falls through to a full date, and
  // 11pm last night read at 1am this morning is two hours ago, so it claims
  // "Today". Same start-of-day comparison formatRelativeUpdated already uses.
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return `Today at ${time}`;
  if (diffDays === 1) return `Yesterday at ${time}`;
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return `${date} at ${time}`;
}

// File sizes the way the mail preview already writes them ("29 KB" — space
// before the unit, matching the dominant house form). KB below 1MB, one
// decimal of MB above, trailing ".0" dropped so 2MB reads "2 MB" not "2.0 MB".
export function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  const mb = n / (1024 * 1024);
  const rounded = Math.round(mb * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)} MB`;
}
